// API Client for Calibrate Dashboard
// Connects to Flask backend serving SQLite data

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000/api';

export interface DashboardSummary {
  totals: {
    revenue: number;
    cost: number;
    margin: number;
    marginPct: number;
  };
  businessUnits: Array<{
    business_unit: string;
    revenue: number;
    cost: number;
    margin: number;
    margin_pct: number;
    pct_of_total_revenue: number;
  }>;
  trend: Array<{
    report_date: string;
    total_revenue: number;
    total_cost: number;
    gross_margin: number;
    margin_pct: number;
  }>;
  dataAsOf: string;
}

export interface MonthlyData {
  month: string;
  month_start: string;
  total_revenue: number;
  total_cost: number;
  gross_margin: number;
  margin_pct: number;
}

export interface ClientData {
  client_display_name: string;
  total_revenue: number;
  pct_of_total_revenue: number;
  total_margin: number;
  margin_pct: number;
  days_active: number;
}

export interface FreightData {
  carrier_name: string;
  total_revenue: number;
  total_cost: number;
  total_margin: number;
  avg_margin_pct: number;
}

/**
 * Fetch main dashboard summary
 * Includes totals, business units, and trend data
 *
 * @param period - 'daily', 'weekly', or 'monthly' (default: 'daily')
 * @param days - number of periods to return (default: 30)
 */
export async function fetchDashboardSummary(
  period: 'daily' | 'weekly' | 'monthly' = 'daily',
  days: number = 30
): Promise<DashboardSummary> {
  try {
    const params = new URLSearchParams({
      period: period,
      days: days.toString()
    });
    
    const response = await fetch(`${API_URL}/dashboard/summary?${params}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch dashboard data');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching dashboard summary:', error);
    throw error;
  }
}

/**
 * Fetch monthly income statement (last 12 months)
 */
export async function fetchMonthlyIncomeStatement(): Promise<MonthlyData[]> {
  try {
    const response = await fetch(`${API_URL}/dashboard/income-statement/monthly`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch monthly data');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching monthly income statement:', error);
    throw error;
  }
}

/**
 * Fetch daily income statement for a specific date
 */
export async function fetchDailyIncomeStatement(date?: string): Promise<any> {
  try {
    const dateParam = date || new Date().toISOString().split('T')[0];
    const response = await fetch(`${API_URL}/dashboard/income-statement/daily?date=${dateParam}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch daily data');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching daily income statement:', error);
    throw error;
  }
}

/**
 * Fetch client performance rankings
 */
export async function fetchClientPerformance(limit: number = 20): Promise<ClientData[]> {
  try {
    const response = await fetch(`${API_URL}/dashboard/clients?limit=${limit}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch client data');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching client performance:', error);
    throw error;
  }
}

/**
 * Fetch freight carrier analysis
 */
export async function fetchFreightAnalysis(): Promise<FreightData[]> {
  try {
    const response = await fetch(`${API_URL}/dashboard/freight`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch freight data');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching freight analysis:', error);
    throw error;
  }
}

// ============================================================================
// CLIENT ANALYTICS
// ============================================================================

/**
 * Fetch top clients ranked by revenue
 * @param limit - Number of clients to return (default: 50)
 */
export async function fetchClientList(limit: number = 50): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/clients?limit=${limit}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch client list');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching client list:', error);
    throw error;
  }
}

/**
 * Fetch detailed analytics for a specific client
 * @param clientName - Name of the client
 */
export async function fetchClientDetail(clientName: string): Promise<any> {
  try {
    const encodedName = encodeURIComponent(clientName);
    const response = await fetch(`${API_URL}/analytics/clients/${encodedName}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch client detail');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching client detail:', error);
    throw error;
  }
}

/**
 * Fetch client business unit mix (all clients)
 */
export async function fetchClientBusinessMix(): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/clients/business-mix`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch business mix');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching client business mix:', error);
    throw error;
  }
}

// ============================================================================
// ORDER ANALYTICS
// ============================================================================

/**
 * Fetch daily order volume metrics
 * @param days - Number of days to return (default: 30)
 */
export async function fetchDailyOrders(days: number = 30): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/orders/daily?days=${days}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch daily orders');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching daily orders:', error);
    throw error;
  }
}

/**
 * Fetch monthly order trends
 * @param months - Number of months to return (default: 12)
 */
export async function fetchMonthlyOrders(months: number = 12): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/orders/monthly?months=${months}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch monthly orders');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching monthly orders:', error);
    throw error;
  }
}

/**
 * Fetch daily operations metrics
 * @param days - Number of days to return (default: 30)
 */
export async function fetchOperationsMetrics(days: number = 30): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/operations/daily?days=${days}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch operations metrics');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching operations metrics:', error);
    throw error;
  }
}

// ============================================================================
// CARRIER/FREIGHT ANALYTICS
// ============================================================================

/**
 * Fetch carrier performance summary
 */
export async function fetchCarrierPerformance(): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/carriers`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch carrier performance');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching carrier performance:', error);
    throw error;
  }
}

/**
 * Fetch carrier monthly trends
 * @param months - Number of months to return (default: 12)
 */
export async function fetchCarrierTrends(months: number = 12): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/carriers/trends?months=${months}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch carrier trends');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching carrier trends:', error);
    throw error;
  }
}

// ============================================================================
// SERVICE ANALYTICS
// ============================================================================

/**
 * Fetch service code performance analysis
 */
export async function fetchServicePerformance(): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/services`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch service performance');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching service performance:', error);
    throw error;
  }
}

/**
 * Fetch service monthly trends
 * @param months - Number of months to return (default: 12)
 */
export async function fetchServiceTrends(months: number = 12): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/services/trends?months=${months}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch service trends');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching service trends:', error);
    throw error;
  }
}

// ============================================================================
// WAREHOUSE/STORAGE ANALYTICS
// ============================================================================

/**
 * Fetch storage and receiving metrics
 * @param months - Number of months to return (default: 12)
 */
export async function fetchStorageMetrics(months: number = 12): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/storage?months=${months}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch storage metrics');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching storage metrics:', error);
    throw error;
  }
}

// ============================================================================
// YEAR-OVER-YEAR COMPARISONS
// ============================================================================

/**
 * Fetch year-over-year monthly comparison
 */
export async function fetchYoYComparison(): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/analytics/yoy`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch YoY comparison');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching YoY comparison:', error);
    throw error;
  }
}

// ============================================================================
// FINANCIAL STATEMENTS
// ============================================================================

/**
 * Fetch income statement for a specific period
 * @param months - Number of months to return (default: 12)
 */
export async function fetchMonthlyIncome(months: number = 12): Promise<any[]> {
  try {
    const response = await fetch(`${API_URL}/dashboard/income-statement/monthly?months=${months}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch monthly income statement');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching monthly income:', error);
    throw error;
  }
}

/**
 * Fetch daily income statement for a specific date
 * @param date - Date in YYYY-MM-DD format (default: today)
 */
export async function fetchDailyIncome(date?: string): Promise<any> {
  try {
    const dateParam = date || new Date().toISOString().split('T')[0];
    const response = await fetch(`${API_URL}/dashboard/income-statement/daily?date=${dateParam}`);
    const json = await response.json();
    
    if (!json.success) {
      throw new Error(json.error || 'Failed to fetch daily income statement');
    }
    
    return json.data;
  } catch (error) {
    console.error('Error fetching daily income:', error);
    throw error;
  }
}

// ============================================================================
// SYSTEM / HEALTH
// ============================================================================

/**
 * Check API health status
 */
export async function checkAPIHealth(): Promise<{ status: string; database: string; timestamp: string }> {
  try {
    const response = await fetch(`${API_URL}/health`);
    const json = await response.json();
    return json;
  } catch (error) {
    console.error('Error checking API health:', error);
    throw error;
  }
}

/**
 * List all available API endpoints
 */
export async function fetchAPIEndpoints(): Promise<any> {
  try {
    const response = await fetch(`${API_URL}/endpoints`);
    const json = await response.json();
    return json;
  } catch (error) {
    console.error('Error fetching API endpoints:', error);
    throw error;
  }
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return '$0.00';
  
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(2)}`;
  }
}

/**
 * Format percentage for display
 */
export function formatPercent(value: number | null | undefined, decimals: number = 1): string {
  if (value == null || isNaN(value)) return '0.0%';
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
